import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, unref as _unref, isRef as _isRef, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "tickets-filter__buttons" }

import { onMounted, ref, watch } from "vue";
import DsButton from "@sazka/design-system/src/button/ds-button.vue";
import TransitionExpand from "@sazka/design-system/src/transitions/transition-expand.vue";
import { useNgSession } from "@sazka/ng/ng-session";
import TicketsFilterFilterMenu from "@/tickets/components/tickets-filter/tickets-filter-filter-menu.vue";
import TicketsFilterSortMenu from "@/tickets/components/tickets-filter/tickets-filter-sort-menu.vue";
import {
    checkedFilters,
    selectedSorting,
    useTicketsFilterHelper,
} from "@/tickets/components/tickets-filter/tickets-filter-state";
import { useShowMoreFeature } from "@/tickets/composables/use-show-more-feature";
import { OnlineTicketsCategoryPageAnalytics } from "@/tickets/utils/online-tickets-analytics-category-page";
import { ScratchTicketsCategoryPageAnalytics } from "@/tickets/utils/scratch-tickets-analytics-category-page";
import { TicketsSortingOption } from "@/tickets/utils/tickets-sorting-option-enum";


export default /*@__PURE__*/_defineComponent({
  __name: '_tickets-filter',
  setup(__props) {

const sortMenuVisible = ref(false);
const filterMenuVisible = ref(false);
const filterMenuValues = ref(new Set<number>());
const { addFilter } = useTicketsFilterHelper();

// Menu controls
function toggleSortMenu() {
    sortMenuVisible.value = !sortMenuVisible.value;
}

function toggleFilterMenu() {
    filterMenuVisible.value = !filterMenuVisible.value;
}

// Card filtering
function filterCards() {
    useShowMoreFeature().hide();

    const cards = document.querySelectorAll<HTMLElement>(
        "#card-list .ds-product-card:not(#random-recommendation-card .ds-product-card)"
    );
    const cardList = document.getElementById("card-list");

    const filtersChecked = checkedFilters.value.length > 0;

    cardList?.classList.toggle("filtered", filtersChecked);

    const { isLoggedIn } = useNgSession();
    // Card is visible if it doesnt have authenticated class eg. its visible to all users (including anonymous) or user is logged in
    const isVisibleByAuthentication = (card: HTMLElement) =>
        !card.classList.contains("authenticated") || isLoggedIn.value;

    cards.forEach((card) => {
        const link = card.querySelector(".ds-product-card__link");

        const price = link ? parseInt(link.getAttribute("data-price") || "0") : 0;

        const isVisible =
            (checkedFilters.value.length === 0 || checkedFilters.value.includes(price)) &&
            isVisibleByAuthentication(card);

        if (isVisible) {
            card.removeAttribute("style");
        } else {
            card.setAttribute("style", "display: none;");
        }
    });
}

watch(
    checkedFilters,
    (newFilters) => {
        filterCards();

        // URL change handling
        const queryParams = new URLSearchParams(window.location.search);

        if (newFilters.length > 0) {
            queryParams.set("filter", Array.from(newFilters).join(","));
        } else {
            queryParams.delete("filter");
        }

        const newUrl = window.location.pathname + (queryParams.toString() ? `?${queryParams.toString()}` : "");
        window.history.replaceState({ path: newUrl }, "", newUrl);

        // Analytics handling
        const sortedFiltersString = [...newFilters].sort((a, b) => a - b).join(",");

        const path = window.location.pathname;
        if (path.startsWith("/losy/stiraci-losy")) {
            ScratchTicketsCategoryPageAnalytics.pushLobbyFilterGameList(sortedFiltersString);
        } else if (path.startsWith("/losy")) {
            OnlineTicketsCategoryPageAnalytics.pushLobbyFilterGameList(sortedFiltersString);
        }
    },
    { deep: true }
);

// Card sorting
function sortCards() {
    const cardContainer = document.querySelector(".cp-lotteries-cards__grid");
    const cards = Array.from(
        document.querySelectorAll("#card-list .ds-product-card:not(#random-recommendation-card .ds-product-card)")
    );

    const cardsData = cards.map((card) => {
        const link = card.querySelector(".ds-product-card__link");
        const price = link ? parseInt(link.getAttribute("data-price") || "0") : 0;
        const prize = link ? parseInt(link.getAttribute("data-prize") || "0") : 0;
        const originalOrder = link ? parseInt(link.getAttribute("data-order") || "0") : 0;
        return { card, price, prize, originalOrder };
    });

    switch (selectedSorting.value) {
        case TicketsSortingOption.Default:
            cardsData.sort((a, b) => a.originalOrder - b.originalOrder);
            break;
        case TicketsSortingOption.PriceAscending:
            cardsData.sort((a, b) => a.price - b.price);
            break;
        case TicketsSortingOption.PriceDescending:
            cardsData.sort((a, b) => b.price - a.price);
            break;
        case TicketsSortingOption.WinAscending:
            cardsData.sort((a, b) => a.prize - b.prize);
            break;
        case TicketsSortingOption.WinDescending:
            cardsData.sort((a, b) => b.prize - a.prize);
            break;
    }

    cardsData.forEach(({ card }) => {
        cardContainer?.appendChild(card);
    });
}

watch(selectedSorting, () => {
    sortCards();
    sortMenuVisible.value = false;

    // Analytics handling
    const path = window.location.pathname;
    if (path.startsWith("/losy/stiraci-losy")) {
        ScratchTicketsCategoryPageAnalytics.pushLobbyFilterGameList(selectedSorting.value);
    } else if (path.startsWith("/losy")) {
        OnlineTicketsCategoryPageAnalytics.pushLobbySortGameList(selectedSorting.value);
    }
});

// Function to extract data-price values and fill filterMenuValues
function constructFilterMenuValues() {
    const cards = document.querySelectorAll("#card-list .ds-product-card a[data-price]");

    const filters: number[] = [];
    cards.forEach((card) => {
        const price = card.getAttribute("data-price");
        filters.push(Number(price));
    });

    filterMenuValues.value = new Set(filters.toSorted((a, b) => a - b));
}

// Lifecycle hooks
onMounted(() => {
    constructFilterMenuValues();

    // URL handling
    const queryParams = new URLSearchParams(window.location.search);
    const filterQuery = queryParams.get("filter");
    if (filterQuery) {
        const filters = new Set(
            filterQuery
                .split(",")
                .map(Number)
                .sort((a, b) => a - b)
        );
        const filtersIntersection = new Set<number>();
        for (const elem of filters) {
            if (filterMenuValues.value.has(elem)) {
                filtersIntersection.add(elem);
            }
        }
        if (filters.size > 0) {
            filtersIntersection.forEach(addFilter);
        }
    }
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _createVNode(DsButton, {
        onClick: toggleFilterMenu,
        type: "custom",
        "icon-left": "money",
        size: "sm",
        class: "tickets-filter__button--filter"
      }, {
        default: _withCtx(() => _cache[1] || (_cache[1] = [
          _createTextVNode("Cena losu ")
        ])),
        _: 1,
        __: [1]
      }),
      _createVNode(DsButton, {
        type: "interaction",
        "icon-left": "sort-by",
        size: "sm",
        "aria-label": "Řazení losů",
        onClick: toggleSortMenu
      }),
      (sortMenuVisible.value)
        ? (_openBlock(), _createBlock(TicketsFilterSortMenu, {
            key: 0,
            modelValue: _unref(selectedSorting),
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => (_isRef(selectedSorting) ? (selectedSorting).value = $event : null)),
            onCloseMenu: toggleSortMenu
          }, null, 8, ["modelValue"]))
        : _createCommentVNode("", true)
    ]),
    _createVNode(TransitionExpand, null, {
      default: _withCtx(() => [
        (filterMenuVisible.value)
          ? (_openBlock(), _createBlock(TicketsFilterFilterMenu, {
              key: 0,
              "filter-values": filterMenuValues.value
            }, null, 8, ["filter-values"]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    })
  ], 64))
}
}

})